import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import "./Modal.css";
import "./PhotoJournalWizardModal.css";

const NEW_SCHEDULE_VALUE = "__new__";

const RadioRow = ({ checked, onChange, children }) => (
  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#374151", padding: "3px 0", cursor: "pointer" }}>
    <input type="radio" checked={checked} onChange={onChange} /> {children}
  </label>
);

// Entry screen for the "Create Journal from Photos" wizard. Photo selection
// itself happens after this: "Saved Files" opens ImageDialog directly (with
// its own empty-state note if there are no files yet); "Google Drive" opens
// a dedicated picker + EXIF/GPS enrichment step, since ImageDialog's own
// built-in Google Drive tab returns files with no location/date metadata,
// which this wizard needs to place photos on the map.
// Two more sources (2026-10-17), below the trip-photo ones: free photos
// from the web and Wikipedia articles, each picked one becoming its own
// place — so photos can start a guide on any topic, not only a trip (e.g. a
// Photo Book of airplanes). The feature keeps its name: it's still mainly
// about turning travel photos into a journal.
const PhotoJournalWizardModal = ({ show, onHide, onSelectFromSaved, onSelectFromGDrive, onSelectFromWeb, onSelectFromWikipedia, onOpenPersonalize }) => {
  // "Where should this go?" — matches BuildMyTripWizard.js's guideTarget
  // model ("A new guide"/"Add to this guide"/"Replace this guide") instead
  // of the old single "Clear and overwrite current guide" toggle, which
  // could only ever act on the current guide and gave no "create a new
  // guide" option at all.
  // Defaults to "add" (2026-10-22): with the web-photo and Wikipedia
  // sources, adding a few places to the open guide is the common case, and
  // the safer one (was "new", like BuildMyTripWizard.js). The schedule
  // sub-choice still defaults to Main.
  const [guideTarget, setGuideTarget] = useState("add"); // "new" | "add" | "replace"
  const [newGuideName, setNewGuideName] = useState("");
  // "Which schedule?" — only shown/used when guideTarget === "add"; new/
  // replace always go into "Main", same rule as BuildMyTripWizard.js.
  const [scheduleTarget, setScheduleTarget] = useState("Main");
  const [groupName, setGroupName] = useState("Photo");
  // Own trip photos mean the user was there (2026-10-23, matches mobile):
  // their places are marked visited unless this is unticked.
  const [markVisited, setMarkVisited] = useState(true);

  const selectGuideTarget = (value) => {
    setGuideTarget(value);
    if (value !== "add") setScheduleTarget("Main");
  };

  const options = { guideTarget, newGuideName: newGuideName.trim(), scheduleTarget, groupName: groupName.trim() || "Photo", markVisited };
  const scheduleNames = window.guide_obj?.schedule?.geScheduleNames() || [];

  const runSaved = () => {
    onHide();
    setTimeout(() => onSelectFromSaved(options), 300);
  };

  const runGDrive = () => {
    onHide();
    setTimeout(() => onSelectFromGDrive(options), 300);
  };

  const runWeb = () => {
    onHide();
    setTimeout(() => onSelectFromWeb(options), 300);
  };

  const runWikipedia = () => {
    onHide();
    setTimeout(() => onSelectFromWikipedia(options), 300);
  };

  return (
    <Modal show={show} onHide={onHide} centered dialogClassName="pjw-dialog">
      <Modal.Body style={{ padding: 0 }}>
        <div className="pjw-shell">
          <div className="pjw-header">
            <div className="pjw-header-icon">
              <FontAwesomeIcon icon={solid("book-open")} />
            </div>
            <h2 className="pjw-title" id="pjw-title">Create Journal from Photos</h2>
            <button className="pjw-close" onClick={onHide}>
              <FontAwesomeIcon icon={solid("xmark")} />
            </button>
          </div>

          <div className="pjw-body pjw-step4-body">
            <p className="pjw-step-desc">
              Pick your trip photos and we'll turn them into places, grouped by location, with a matching schedule.
            </p>

            <p className="pjw-section-label">Where should this go?</p>
            <RadioRow checked={guideTarget === "new"} onChange={() => selectGuideTarget("new")}>A new guide</RadioRow>
            {guideTarget === "new" && (
              <div className="pjw-field-row">
                <label className="pjw-field-label" htmlFor="pjw-new-guide-name">Guide Name</label>
                <input
                  id="pjw-new-guide-name"
                  className="pjw-field-input"
                  type="text"
                  value={newGuideName}
                  onChange={e => setNewGuideName(e.target.value)}
                  placeholder="Auto-generated from folder name"
                />
              </div>
            )}
            <RadioRow checked={guideTarget === "add"} onChange={() => selectGuideTarget("add")}>Add to this guide</RadioRow>
            <RadioRow checked={guideTarget === "replace"} onChange={() => selectGuideTarget("replace")}>Replace this guide</RadioRow>

            {guideTarget === "add" && (
              <>
                <p className="pjw-section-label" style={{ marginTop: 8 }}>Which schedule?</p>
                <RadioRow checked={scheduleTarget === NEW_SCHEDULE_VALUE} onChange={() => setScheduleTarget(NEW_SCHEDULE_VALUE)}>
                  Create a new schedule
                </RadioRow>
                {scheduleTarget === NEW_SCHEDULE_VALUE && (
                  <div className="pjw-field-row">
                    <label className="pjw-field-label" htmlFor="pjw-group-name">Name</label>
                    <input
                      id="pjw-group-name"
                      className="pjw-field-input"
                      type="text"
                      value={groupName}
                      onChange={e => setGroupName(e.target.value)}
                      placeholder="Photo"
                      maxLength={40}
                    />
                  </div>
                )}
                {scheduleNames.map((s) => (
                  <RadioRow key={s.value} checked={scheduleTarget === s.value} onChange={() => setScheduleTarget(s.value)}>
                    Add to "{s.label}"
                  </RadioRow>
                ))}
              </>
            )}

            <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer", marginTop: 8 }}>
              <input type="checkbox" checked={markVisited} onChange={(e) => setMarkVisited(e.target.checked)} />
              Mark these places as visited
            </label>

            <div className="pjw-step4-divider" />

            <button className="pjw-source-btn" onClick={runSaved}>
              <FontAwesomeIcon icon={solid("cloud")} className="pjw-source-icon" />
              Add from Saved Files
            </button>

            {onSelectFromGDrive && (
              <button className="pjw-source-btn" onClick={runGDrive}>
                <FontAwesomeIcon icon={solid("hard-drive")} className="pjw-source-icon" />
                Add from Google Drive
              </button>
            )}

            {(onSelectFromWeb || onSelectFromWikipedia) && (
              <p className="pjw-section-label pjw-web-label">Or start from photos on the web — each becomes a place</p>
            )}

            {onSelectFromWeb && (
              <button className="pjw-source-btn" onClick={runWeb}>
                <FontAwesomeIcon icon={solid("image")} className="pjw-source-icon" />
                Free photos from the web
              </button>
            )}

            {onSelectFromWikipedia && (
              <button className="pjw-source-btn" onClick={runWikipedia}>
                <FontAwesomeIcon icon={solid("book-open")} className="pjw-source-icon" />
                Wikipedia articles
              </button>
            )}

            {onOpenPersonalize && (
              <p className="pjw-personalize-hint">
                Already have places in this guide?{" "}
                <button className="pjw-personalize-link" onClick={() => { onHide(); onOpenPersonalize(); }}>
                  Try Personalize Guide with Photos
                </button>{" "}
                instead.
              </p>
            )}

            <div className="pjw-step4-divider" />

            <button className="pjw-cancel-btn" onClick={onHide}>Cancel</button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PhotoJournalWizardModal;
